import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { SmartCaptcha } from '@yandex/smart-captcha';
import { useEffect, useState } from 'react';
import { useCreateLeadMutation } from 'app/api/lead';
import * as Yup from 'yup';
import { useAlertHook } from 'app/hooks/useAlertHook';
import InputMask from 'react-input-mask';
import { Alert } from 'app/components/Alerts/Alerts';
import SimpleGDPR from 'simple-gdpr';
import { useLocalStorage } from 'app/hooks';
import { useCheckDevice } from 'app/helpers/useCheckDevice';
import { FullImageBlock } from './components/FullImageBlock/FullImageBlock';
import { ContentBlock, WelcomeThemeEnum } from './components/TextImageBlock/ContentBlock';
import { Footer } from './components/Footer/Footer';

import shippingMapImage from '../../assets/images/wp_images/wp_shipping_map.jpg';
import fleetManagementImage from '../../assets/images/wp_images/wp_fleet_management.jpg';
import vesselDetailsImage from '../../assets/images/wp_images/wp_vessel_details.jpg';
import areaMonitoringImage from '../../assets/images/wp_images/wp_area_monitoring.jpg';
import dataAnalyticsImage from '../../assets/images/wp_images/wp_data_analytics.jpg';
import temperatireImage from '../../assets/images/wp_images/wp_temperature.jpg';
import techSolutionImage from '../../assets/images/wp_images/wp_tech_solution.jpg';

import {
  areaMonitoringList,
  dataAnalyticsList,
  fleetManagementList,
  mainFunctions,
  salesBlock,
  shippingMapList,
  supportTextBlock,
  techSolution,
  temperatureList,
  vesselDetailsList,
} from './texts/texts';

import clsx from 'clsx';
import styles from './WelcomePage.module.scss';
import './cookies.css';
import { Header } from './components/Header/Header';
import { Icon } from 'app/components';
import ym from 'react-yandex-metrika';

const aboutTextBlock = () => {
  const listItems = [
    'Детальная информация о выбранном судне и его передвижении;',
    'Ситуация на Северном морском пути и в Южном океане;',
    'Обстановка в районах рыболовного промысла и торговых коридорах;',
    'Обширный каталог судов и портов мира;',
    'Анализ данных, уведомления и оповещения;',
    'Интеграция с внешними системами через программные API-интерфейсы.',
  ];
  return listItems.map((item, key) => (
    <li className={styles.list_item} key={key}>
      {item}
    </li>
  ));
};

export const WelcomePage = () => {
  const { addAlert } = useAlertHook();

  const { isMobile, isTablet } = useCheckDevice();

  const documents: string[] | null = window._env_?.DOCUMENTS ? JSON.parse(window._env_.DOCUMENTS) : null;

  const [cookiesAccepted, setCookiesAccepted] = useLocalStorage('cookiesAccepted', false);

  const [token, setToken] = useState<string | null>(null);
  const [isFormSent, setIsFormSent] = useState<boolean>(false);
  const [isAlternativeHeader, setIsAleternativeHeader] = useState<boolean | undefined>(undefined);

  const [createLead, { ...createLeadRequest }] = useCreateLeadMutation();
  const sitekey: string = window._env_?.CAPTCHA_SITEKEY;

  const phoneRegExp = /^\+[0-9]{1,3}[(]?[0-9]{1,4}[)]?[-\s\\.]?[0-9]{3}[-\s\\.]?[0-9]{3,6}$/;

  const validationSchema = Yup.object().shape({
    fio: Yup.string().required().max(256),
    companyName: Yup.string().required().max(256),
    email: Yup.string().required().email(),
    phone: Yup.string().required().matches(phoneRegExp),
    isAgreed: Yup.bool().oneOf([true]),
  });

  const initialFormValues = {
    fio: '',
    companyName: '',
    email: '',
    phone: '',
    isAgreed: false,
    token: '',
  };

  const handleSubmit = (
    values: typeof initialFormValues,
    { resetForm }: FormikHelpers<typeof initialFormValues>,
  ): void | Promise<any> => {
    const { fio, companyName, phone, email } = values;

    createLead({
      body: {
        fio,
        companyName,
        phone,
        email,
      },
      token,
    })
      .unwrap()
      .then(() => {
        setIsFormSent(true);
        resetForm();
      })
      .catch((err) => {
        addAlert(err);
      });
  };

  useEffect(() => {
    if (!cookiesAccepted) {
      const notice = new SimpleGDPR({
        title: 'Этот сайт использует cookies',
        message:
          'Мы используем файлы cookies и другие технологии для предоставления возможности использовать персональные настройки сайта. Нажимая кнопку "Принять" вы даете согласие на их примерение. Вся функциональность сайта привязана к конкретному пользователю, соответственно, отказ приведет к невозможности его использования.',
        link: null,
        openOnInit: true,
        callback: () => {
          setCookiesAccepted(true);
          notice.close();
        },
        id: {
          box: 'cookies-content-box',
          button: 'cookies-content-button',
        },
      });
      const buttonEl = document.getElementById('cookies-content-button');

      if (buttonEl?.innerHTML) {
        buttonEl.innerHTML = 'Принять';
      }
    }
  }, [cookiesAccepted]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY >= 70) {
        setIsAleternativeHeader(true);
      } else {
        setIsAleternativeHeader(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isAlternativeHeader]);

  useEffect(() => {
    ym('reachGoal', 'landingGoal');
  }, []);
  return (
    <div
      className={clsx(styles.root, {
        [styles.mobile]: !!isMobile,
        [styles.tablet]: !!isTablet,
      })}
    >
      <Header cookiesAccepted={cookiesAccepted} isAlternativeHeader={isAlternativeHeader} />
      <div className={styles.text_block}>
        <h1 className={styles.title}>SiAIS</h1>
        <p className={styles.sub_title}>
          Российский сервис мониторинга надводной обстановки на основе АИС-данных, полученных спутниковой группировкой Sitronics.
        </p>
      </div>
      <div className={styles.about}>
        <div className={styles.image_wrapper}>
          <FullImageBlock />
        </div>
        <div className={styles.text_wrapper}>
          <h1 className={styles.title}>SiAIS это:</h1>
          <ul className={styles.list}>{aboutTextBlock()}</ul>
        </div>
      </div>
      <div className={styles.video} aria-label="SiAIS video demostraion">
        <div className={styles.video_wrapper}>
          <iframe
            width="100%"
            height="100%"
            src="https://rutube.ru/play/embed/cee1700747ca0aed2ed701a17bae99ff?skinColor=363849"
            allow="autoplay"
            allowFullScreen
            frameBorder={0}
          ></iframe>
        </div>
      </div>
      <div id="functions">
        <ContentBlock title={mainFunctions.title} content={mainFunctions.text} theme={WelcomeThemeEnum.Light} fullSize={true} />
      </div>
      <ContentBlock
        title={shippingMapList.title}
        image={shippingMapImage}
        content={shippingMapList.text}
        theme={WelcomeThemeEnum.Light}
        placement={isTablet || isMobile ? 'revert' : 'normal'}
      />
      <ContentBlock
        title={fleetManagementList.title}
        image={fleetManagementImage}
        content={fleetManagementList.text}
        theme={WelcomeThemeEnum.Light}
        placement="revert"
      />
      <ContentBlock
        title={vesselDetailsList.title}
        image={vesselDetailsImage}
        content={vesselDetailsList.text}
        theme={WelcomeThemeEnum.Light}
        placement={isTablet || isMobile ? 'revert' : 'normal'}
      />
      <ContentBlock
        title={areaMonitoringList.title}
        image={areaMonitoringImage}
        content={areaMonitoringList.text}
        theme={WelcomeThemeEnum.Light}
        placement="revert"
      />
      <ContentBlock
        title={dataAnalyticsList.title}
        image={dataAnalyticsImage}
        content={dataAnalyticsList.text}
        theme={WelcomeThemeEnum.Light}
        placement={isTablet || isMobile ? 'revert' : 'normal'}
      />
      <ContentBlock
        title={temperatureList.title}
        image={temperatireImage}
        placement="revert"
        content={temperatureList.text}
        theme={WelcomeThemeEnum.Light}
      />
      <div id="tech_solution">
        <ContentBlock
          title={techSolution.title}
          image={techSolutionImage}
          content={techSolution.text}
          theme={WelcomeThemeEnum.Dark}
          fullSize={true}
        />
      </div>
      <div id="contacts">
        <ContentBlock
          title={supportTextBlock.title}
          content={supportTextBlock.text}
          theme={WelcomeThemeEnum.Blue}
          fullSize={true}
        />
      </div>
      <div className={styles.form_block}>
        <div className={styles.form_body}>
          <h1 className={styles.title}>ЗАПРОС НА ПРИОБРЕТЕНИЕ</h1>
          <div className={styles.sub_title}>Подать заявку на подключение к SiAIS:</div>
          {isFormSent ? (
            <div className={styles.formSuccessMessage}>
              <h4>Ваша заявка принята</h4>
              <span>С Вами свяжутся в ближайшее время</span>
            </div>
          ) : (
            <Formik
              initialValues={initialFormValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnChange={true}
              isInitialValid={false}
            >
              {({ isValid, errors, touched }) => (
                <Form>
                  <div className={clsx(styles.input_wrapper, { [styles.error]: errors.fio && touched.fio })}>
                    <label htmlFor="fio">ФИО:</label>
                    <Field
                      id="fio"
                      type="text"
                      name="fio"
                      placeholder="Фамилия, имя, отчество"
                      disabled={createLeadRequest.isLoading}
                    />
                  </div>
                  <div className={clsx(styles.input_wrapper, { [styles.error]: errors.companyName && touched.companyName })}>
                    <label htmlFor="companyName">Компания:</label>
                    <Field
                      if="companyName"
                      type="text"
                      name="companyName"
                      placeholder="Название организации"
                      disabled={createLeadRequest.isLoading}
                    />
                  </div>
                  <div className={clsx(styles.input_wrapper, { [styles.error]: errors.email && touched.email })}>
                    <label htmlFor="email">Email:</label>
                    <Field type="email" name="email" placeholder="Адрес" disabled={createLeadRequest.isLoading} />
                  </div>
                  <div className={clsx(styles.input_wrapper, { [styles.error]: errors.phone && touched.phone })}>
                    <label htmlFor="phone">Телефон:</label>
                    <Field id="phone" name="phone" disabled={createLeadRequest.isLoading}>
                      {(props: FieldProps) => {
                        const mask = '+7(999)999-9999';
                        return <InputMask {...props.field} mask={mask} placeholder="Мобильный телефон" alwaysShowMask={false} />;
                      }}
                    </Field>
                  </div>
                  {/* <div className={styles.captcha}>
                    {sitekey && (
                      <SmartCaptcha
                        sitekey={sitekey}
                        onSuccess={setToken}
                        onTokenExpired={() => {
                          setToken(null);
                        }}
                      />
                    )}
                  </div> */}
                  <div className={styles.consent}>
                    <label className={styles.consent_label}>
                      <Field type="checkbox" name="isAgreed" className={styles.checkbox} disabled={createLeadRequest.isLoading} />
                      <span className={styles.consent_text}>
                        Даю согласие на обработку указанных в заявке данных организациями, входящими в группу лиц АО «Ситроникс» и
                        ПАО «АФК „Система“»
                      </span>
                    </label>
                  </div>
                  <div className={styles.request_button_wrapper}>
                    <button className={styles.button} type="submit" disabled={!isValid || (Boolean(sitekey) && !token)}>
                      {createLeadRequest.isLoading ? (
                        <span>
                          <Icon name="loader" size="16" />
                          <span>Создание заявки</span>
                        </span>
                      ) : (
                        'Подать заявку'
                      )}
                    </button>
                  </div>
                  <div className={styles.required_text}>Все поля обязательны для заполнения.</div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
      <ContentBlock title={salesBlock.title} content={salesBlock.text} fullSize={true} theme={WelcomeThemeEnum.Light} />
      {documents && documents.length > 0 && (
        <ContentBlock title="Документация" documents={documents} theme={WelcomeThemeEnum.Light} fullSize={true} />
      )}
      {/* {version && <div className={styles.version}>Текущая версия программного обеспечения: {version}</div>} */}
      <Footer theme={WelcomeThemeEnum.Dark} />
      <Alert closable={false} className={styles.alert_message} />
    </div>
  );
};
